import { Button, Icon } from '@ds/core';
import { Menu } from '@ds/navigation';
import { useAviso } from './toast';
import { abrirJanelaDeImpressao, exportarComo } from './exportar';
import type { CargaExportavel, FormatoDeExportacao } from './exportar';

export interface ExportMenuProps<Linha> {
    label?: string;
    variant?: 'primary' | 'secondary' | 'ghost';
    size?: 'sm' | 'md';
    payload: CargaExportavel<Linha>;
    /**
     * Busca TODOS os registros do filtro atual. Sem ela, sai o que está em
     * `payload.rows` — que numa lista paginada é só a página aberta, enquanto o
     * subtítulo dizia o total.
     */
    carregarTudo?: () => Promise<Linha[]>;
}

/** Botão de exportar em três formatos, com aviso do resultado. */
export function ExportMenu<Linha>({ label = 'Exportar', variant = 'secondary', size = 'sm', payload, carregarTudo }: ExportMenuProps<Linha>) {
    const aviso = useAviso();

    const exportar = async (formato: FormatoDeExportacao) => {
        /* A janela do PDF abre antes de qualquer espera: depois dela, o navegador
           a trata como pop-up e bloqueia. */
        const janela = formato === 'pdf' ? abrirJanelaDeImpressao() : undefined;
        if (formato === 'pdf' && !janela) {
            aviso({ title: 'Permita pop-ups para gerar o PDF', description: payload.title, tone: 'warning' });
            return;
        }
        try {
            const rows = carregarTudo ? await carregarTudo() : payload.rows;
            const mensagem = exportarComo(formato, { ...payload, rows }, janela);
            aviso({ title: mensagem, description: `${payload.title} · ${rows.length} ${rows.length === 1 ? 'registro' : 'registros'}`, tone: 'info' });
        } catch (e) {
            janela?.close();
            aviso({ title: 'Não foi possível exportar', description: e instanceof Error ? e.message : undefined, tone: 'danger' });
        }
    };

    return (
        <Menu
            align="right" width={210}
            onSelect={(formato) => { void exportar(formato as FormatoDeExportacao); }}
            trigger={
                <Button
                    variant={variant} size={size}
                    iconLeft={<Icon name="download" size={size === 'sm' ? 14 : 15} />}
                    iconRight={<Icon name="chevron-down" size={13} />}
                >
                    {label}
                </Button>
            }
            items={[
                { id: 'csv', label: 'CSV', icon: 'file-text', hint: '.csv' },
                { id: 'xlsx', label: 'Planilha Excel', icon: 'sheet', hint: '.xls' },
                { divider: true },
                { id: 'pdf', label: 'PDF', icon: 'file-down', hint: 'imprimir' },
            ]}
        />
    );
}
